(() => {
  'use strict';

  class BirthRiskCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      const root = this;
      const nativeDocument = window.document;
      const document = {
        getElementById(id) {
          return root.querySelector(`[data-id="${id}"]`);
        },
        querySelector(selector) {
          return root.querySelector(selector);
        },
        querySelectorAll(selector) {
          return root.querySelectorAll(selector);
        },
        createElement: nativeDocument.createElement.bind(nativeDocument)
      };

      /* ---------------- DATA MODEL ---------------- */
      /* breed risk tiers -> base points
         V very high | H high | M moderate | L low | ? other/unlisted */
      const TIER = {V:55, H:38, M:22, L:4};
      
      const SPECIES = {
        dog:{icon:"🐕", label:"Dog", dam:"bitch", baby:"puppies", birth:"whelp",
          normal:[4,8], multi:true, singleThresh:1, bigLitter:10,
          breeds:[
            {n:"English / British Bulldog",t:"V"},{n:"French Bulldog",t:"V"},{n:"Boston Terrier",t:"V"},
            {n:"Pug",t:"V"},{n:"Scottish Terrier",t:"V"},{n:"Dogue de Bordeaux",t:"V"},{n:"Clumber Spaniel",t:"V"},
            {n:"Chihuahua",t:"H"},{n:"Pekingese",t:"H"},{n:"Pomeranian",t:"H"},{n:"Yorkshire Terrier",t:"H"},
            {n:"Shih Tzu",t:"H"},{n:"Boxer",t:"H"},{n:"Bull Terrier",t:"H"},{n:"Mastiff / Bullmastiff",t:"H"},
            {n:"Miniature Dachshund",t:"H"},{n:"Cavalier King Charles Spaniel",t:"H"},
            {n:"Staffordshire Bull Terrier",t:"M"},{n:"Chow Chow",t:"M"},{n:"Miniature Poodle",t:"M"},
            {n:"Corgi (Pembroke/Cardigan)",t:"M"},{n:"Jack Russell Terrier",t:"M"},
            {n:"Labrador Retriever",t:"L"},{n:"Golden Retriever",t:"L"},{n:"German Shepherd",t:"L"},
            {n:"Border Collie",t:"L"},{n:"Kelpie",t:"L"},{n:"Standard Poodle",t:"L"},{n:"Beagle",t:"L"},
            {n:"Cocker Spaniel",t:"L"},{n:"Rottweiler",t:"L"},{n:"Greyhound / Whippet",t:"L"},
            {n:"Dalmatian",t:"L"},{n:"Australian Shepherd",t:"L"},{n:"Cattle Dog",t:"L"},
            {n:"Other / mixed breed",t:"?"}
          ]},
        cat:{icon:"🐈", label:"Cat", dam:"queen", baby:"kittens", birth:"queen",
          normal:[3,5], multi:true, singleThresh:1, bigLitter:7,
          breeds:[
            {n:"Persian",t:"H"},{n:"Exotic Shorthair",t:"H"},{n:"Himalayan",t:"H"},
            {n:"British Shorthair",t:"H"},{n:"Scottish Fold",t:"H"},
            {n:"Siamese",t:"M"},{n:"Oriental",t:"M"},{n:"Burmese",t:"M"},{n:"Devon Rex",t:"M"},
            {n:"Domestic Shorthair / Longhair",t:"L"},{n:"Maine Coon",t:"L"},{n:"Ragdoll",t:"L"},
            {n:"Bengal",t:"L"},{n:"Russian Blue",t:"L"},{n:"Norwegian Forest",t:"L"},{n:"Sphynx",t:"L"},
            {n:"Other / moggy",t:"?"}
          ]},
        cattle:{icon:"🐄", label:"Cattle", dam:"cow", baby:"calf", birth:"calve",
          normal:[1,1], multi:false, singleBirth:true, twinRisk:20,
          breeds:[
            {n:"Belgian Blue",t:"V"},{n:"Charolais",t:"H"},{n:"Blonde d'Aquitaine",t:"H"},
            {n:"Limousin",t:"H"},{n:"Simmental",t:"M"},{n:"Piedmontese",t:"H"},
            {n:"Hereford",t:"L"},{n:"Angus",t:"L"},{n:"Jersey",t:"M"},{n:"Holstein-Friesian",t:"L"},
            {n:"Wagyu",t:"L"},{n:"Brahman",t:"L"},{n:"Shorthorn",t:"L"},
            {n:"Other / not listed",t:"?"}
          ]},
        sheep:{icon:"🐑", label:"Sheep", dam:"ewe", baby:"lambs", birth:"lamb",
          normal:[1,2], multi:true, singleThresh:1, bigLitter:3, singleBigRisk:true,
          breeds:[
            {n:"Texel",t:"V"},{n:"Beltex",t:"V"},{n:"Charollais",t:"H"},{n:"Suffolk",t:"M"},
            {n:"Dorset",t:"M"},{n:"Merino",t:"L"},{n:"Border Leicester",t:"L"},{n:"Romney",t:"L"},
            {n:"Dorper",t:"L"},{n:"Corriedale",t:"L"},{n:"Other / not listed",t:"?"}
          ]},
        goat:{icon:"🐐", label:"Goat", dam:"doe", baby:"kids", birth:"kid",
          normal:[1,2], multi:true, singleThresh:1, bigLitter:3, singleBigRisk:true,
          breeds:[
            {n:"Boer",t:"H"},{n:"Kiko",t:"M"},{n:"Nubian",t:"L"},{n:"Saanen",t:"L"},
            {n:"Alpine",t:"L"},{n:"Toggenburg",t:"L"},{n:"Pygmy / Nigerian Dwarf",t:"M"},
            {n:"Other / not listed",t:"?"}
          ]},
        horse:{icon:"🐎", label:"Horse", dam:"mare", baby:"foal", birth:"foal",
          normal:[1,1], multi:false, singleBirth:true, twinRisk:62, emergency:true,
          breeds:[
            {n:"Thoroughbred",t:"L"},{n:"Standardbred",t:"L"},{n:"Warmblood",t:"L"},
            {n:"Quarter Horse",t:"L"},{n:"Arabian",t:"L"},{n:"Draft breed (Clydesdale etc.)",t:"M"},
            {n:"Miniature horse / pony",t:"M"},{n:"Other / not listed",t:"?"}
          ]},
        pig:{icon:"🐖", label:"Pig", dam:"sow", baby:"piglets", birth:"farrow",
          normal:[8,14], multi:true, singleThresh:2, bigLitter:16,
          breeds:[
            {n:"Large White / Yorkshire",t:"L"},{n:"Landrace",t:"L"},{n:"Duroc",t:"L"},
            {n:"Berkshire",t:"L"},{n:"Kunekune",t:"M"},{n:"Other / not listed",t:"?"}
          ]}
      };
      
      /* factor definitions per species. common + specific. pts = points */
      const COMMON = [
        {id:"csection",  t:"Previous caesarean",            d:"Especially if it was for size/conformation", pts:30},
        {id:"dystocia",  t:"Previous difficult birth",       d:"Needed help or intervention last time",      pts:22},
        {id:"pelvis",    t:"Known pelvic injury / narrow pelvis", d:"Old fracture or malunion narrows the canal", pts:35},
        {id:"obese",     t:"Overweight / over-conditioned",  d:"Fat crowds the canal and weakens contractions", pts:15},
        {id:"unwell",    t:"Currently unwell",               d:"Systemic illness, fever, low calcium risk",  pts:15},
        {id:"age",       t:"Older for the species",          d:"Aging uterus contracts less effectively",    pts:10}
      ];
      const SPECIFIC = {
        dog:[{id:"maiden",t:"First litter, later in life",d:"Maiden bitch past her prime breeding age",pts:10}],
        cat:[{id:"maiden",t:"First litter, older queen",d:"Maiden queen bred later than ideal",pts:10}],
        cattle:[
          {id:"heifer",t:"First-calf heifer",d:"Immature pelvis, higher dystocia rate",pts:18},
          {id:"biggsire",t:"Sire much larger than dam",d:"Large-breed bull over a smaller/younger dam",pts:24},
          {id:"dmuscle",t:"Double-muscled sire",d:"e.g. Belgian Blue / Charolais bull",pts:22}
        ],
        sheep:[
          {id:"ewelamb",t:"Ewe lamb (first lambing)",d:"Immature, higher dystocia risk",pts:16},
          {id:"ring",t:"Ringwomb risk / history",d:"Cervix fails to fully dilate",pts:22},
          {id:"bigsire",t:"Terminal / big-headed sire",d:"e.g. Texel or Beltex ram over smaller ewe",pts:18}
        ],
        goat:[
          {id:"doeling",t:"First kidding (doeling)",d:"Immature pelvis",pts:14},
          {id:"ring",t:"Ringwomb risk / history",d:"Cervix fails to fully dilate",pts:20}
        ],
        horse:[
          {id:"maidenm",t:"Maiden mare",d:"First foaling",pts:12},
          {id:"bigstal",t:"Small mare, large stallion",d:"Oversized foal for the mare",pts:30},
          {id:"redbag",t:"Placenta / red-bag concern",d:"Premature placental separation history",pts:20}
        ],
        pig:[
          {id:"parity",t:"High parity / older sow",d:"Many previous litters, weaker contractions",pts:15},
          {id:"slow",t:"History of slow farrowing",d:"Prolonged births, uterine inertia",pts:18}
        ]
      };
      
      /* ---------------- STATE ---------------- */
      let S = {species:null};
      
      /* ---------------- BUILD UI ---------------- */
      const el = id => document.getElementById(id);
      
      const grid = el("speciesGrid");
      Object.keys(SPECIES).forEach(k=>{
        const s = SPECIES[k];
        const d = document.createElement("div");
        d.className="sp"; d.dataset.k=k;
        d.innerHTML = `<span class="ico">${s.icon}</span>${s.label}`;
        d.onclick=()=>pickSpecies(k,d);
        grid.appendChild(d);
      });
      
      function pickSpecies(k,node){
        S.species=k;
        document.querySelectorAll(".sp").forEach(n=>n.classList.remove("on"));
        node.classList.add("on");
        const s=SPECIES[k];
      
        // breed dropdown
        const bsel=el("breed"); bsel.innerHTML="";
        s.breeds.forEach(b=>{
          const o=document.createElement("option"); o.value=b.t; o.textContent=b.n; o.dataset.name=b.n; bsel.appendChild(o);
        });
        bsel.onchange=onBreedChange; onBreedChange();
        el("breedTitle").textContent = (k==="dog"||k==="cat") ? "Breed & conformation" : "Breed";
      
        // litter card text
        const cap = s.baby.charAt(0).toUpperCase()+s.baby.slice(1);
        if(s.singleBirth){
          el("litterTitle").textContent="How many "+ (k==="horse"?"foals":"calves") +"?";
          el("litterHint").textContent = `${s.label} normally carry a single ${s.baby}. Twins sharply raise the risk of malpresentation and dystocia.`;
          el("countLabel").textContent="Number of "+(k==="horse"?"foals":"calves")+" expected";
        } else {
          el("litterTitle").textContent="How many "+s.baby+"?";
          el("litterHint").textContent = `Typical ${s.label.toLowerCase()} ${s.multi?"litter":"litter"}: ${s.normal[0]}–${s.normal[1]}. A single oversized ${singular(s.baby)} and unusually large litters both raise risk.`;
          el("countLabel").textContent="Number of "+s.baby+" expected";
        }
        el("count").value="";
      
        // factors
        buildFactors(k);
      
        // reveal
        ["cardBreed","cardLitter","cardFactors"].forEach(id=>el(id).classList.remove("hidden"));
        el("result").classList.add("hidden");
        el("errMsg").classList.add("hidden");
        el("cardBreed").scrollIntoView({behavior:"smooth",block:"nearest"});
      }
      
      function singular(w){
        if(w==="puppies")return "puppy"; if(w==="kittens")return "kitten";
        if(w==="lambs")return "lamb"; if(w==="kids")return "kid";
        if(w==="piglets")return "piglet"; if(w==="calf")return "calf"; if(w==="foal")return "foal";
        return w;
      }
      
      function onBreedChange(){
        const t=el("breed").value;
        el("otherConf").classList.toggle("hidden", t!=="?" || S.species==="cattle"||S.species==="sheep"||S.species==="goat"||S.species==="horse"||S.species==="pig");
      }
      
      function buildFactors(k){
        const host=el("factorHost"); host.innerHTML="";
        const spec=SPECIFIC[k]||[];
        if(spec.length){
          host.appendChild(groupTitle(SPECIES[k].label+" specific"));
          spec.forEach(f=>host.appendChild(chk(f)));
        }
        host.appendChild(groupTitle("General"));
        COMMON.forEach(f=>host.appendChild(chk(f)));
      }
      function groupTitle(txt){const d=document.createElement("div");d.className="grp-title";d.textContent=txt;return d;}
      function chk(f){
        const l=document.createElement("label"); l.className="chk"; l.dataset.pts=f.pts; l.dataset.id=f.id;
        l.innerHTML=`<input type="checkbox"><div><div class="t">${f.t}</div><div class="d">${f.d}</div></div>`;
        const box=l.querySelector("input");
        box.onchange=()=>l.classList.toggle("on",box.checked);
        return l;
      }
      
      /* ---------------- SCORING ---------------- */
      function baseFromBreed(){
        const sel=el("breed"); const t=sel.value;
        const name=sel.options[sel.selectedIndex].dataset.name;
        if(t!=="?") return {pts:TIER[t], label:name, tier:t};
        // Other -> conformation (only for dog/cat)
        if(S.species==="dog"||S.species==="cat"){
          const head=el("headShape").value, size=el("sizeCat").value;
          let p=4, note="typical build";
          if(head==="brachy"){ p = (size==="toy"||size==="small")?46:41; note="flat-faced"; }
          else if(head==="long"){ p = (size==="toy"||size==="small")?14:6; note="long muzzle"; }
          else { p = size==="toy"?20 : size==="small"?12 : 4; note="normal muzzle"; }
          return {pts:p, label:`Unlisted (${note}, ${size})`, tier:p>=40?"V":p>=22?"H":p>=12?"M":"L"};
        }
        return {pts:10, label:"Unlisted breed", tier:"M"};
      }
      
      function litterScore(n, s){
        // returns {pts, reasons:[{txt,kind}]}
        let pts=0; const r=[];
        if(isNaN(n)||n<=0){ return {pts:6, reasons:[{txt:"Number of "+s.baby+" not entered — confirm with x-ray near term for a firm plan.",kind:"warn"}]}; }
        if(s.singleBirth){
          if(n>=2){ pts+=s.twinRisk; r.push({txt:`Twin pregnancy in a ${s.dam} — high risk of malpresentation and dystocia; ${S.species==="horse"?"a veterinary emergency":"often needs assistance or surgery"}.`,kind:"warn"}); }
          else { r.push({txt:`Single ${singular(s.baby)} as expected for a ${s.dam}.`,kind:"ok"}); }
          return {pts,reasons:r};
        }
        if(n<=s.singleThresh){
          pts+=30; r.push({txt:`Very small litter (${n}) — the "single-${singular(s.baby)} effect": one oversized fetus plus a weak trigger for labour. A leading reason for planned caesareans.`,kind:"warn"});
        } else if(n===s.singleThresh+1 && s.singleThresh===1){
          pts+=12; r.push({txt:`Small litter (${n}) — fetuses tend to be larger and can be harder to pass.`,kind:"warn"});
        } else if(n>=s.bigLitter){
          pts+=13; r.push({txt:`Large litter (${n}) — raises the risk of uterine fatigue (secondary inertia) and stalled labour late in the birth.`,kind:"warn"});
        } else {
          r.push({txt:`Litter size (${n}) sits in the normal range for the breed.`,kind:"ok"});
        }
        return {pts,reasons:r};
      }
      
      function calc(){
        const s=SPECIES[S.species];
        const b=baseFromBreed();
        const n=parseInt(el("count").value,10);
        const lit=litterScore(n,s);
      
        let total=b.pts+lit.pts;
        const reasons=[];
      
        // breed reason
        if(b.tier==="V") reasons.push({txt:`${b.label}: a breed where planned caesareans are common — big-headed, big-shouldered babies and a tight maternal pelvis.`,kind:"warn"});
        else if(b.tier==="H") reasons.push({txt:`${b.label}: a higher-risk breed for obstructed birth.`,kind:"warn"});
        else if(b.tier==="M") reasons.push({txt:`${b.label}: a moderate-risk breed — watch closely but natural birth is often possible.`,kind:""});
        else reasons.push({txt:`${b.label}: not a breed with elevated conformational birth risk.`,kind:"ok"});
      
        lit.reasons.forEach(x=>reasons.push(x));
      
        // factors
        document.querySelectorAll("#factorHost .chk").forEach(l=>{
          if(l.querySelector("input").checked){
            total+=parseInt(l.dataset.pts,10);
            const t=l.querySelector(".t").textContent;
            reasons.push({txt:t+" — adds to the risk.",kind:"warn"});
          }
        });
      
        total=Math.min(total,100);
        render(total,reasons,s,b,n);
      }
      
      /* ---------------- RENDER ---------------- */
      function category(score){
        if(score<25) return {k:"Low",  c:"var(--brc-low)",  h:"Natural birth likely",
          p:"On these factors, a normal (unassisted) birth is the most likely outcome. Prepare for a standard birth watch."};
        if(score<50) return {k:"Moderate", c:"var(--brc-moderate)", h:"Watch closely, be ready",
          p:"Natural birth is possible, but there is enough risk to warrant a close birth watch and a vet on standby. Have a plan before labour starts."};
        if(score<72) return {k:"High", c:"var(--brc-high)", h:"Plan for a likely caesarean",
          p:"A planned caesarean is a serious consideration here. Book a pre-birth vet assessment and agree a surgery plan in advance rather than waiting for trouble."};
        return {k:"Very High", c:"var(--brc-very-high)", h:"Elective caesarean usually the safe plan",
          p:"On these factors, an elective (scheduled) caesarean is commonly the safest route. Speak to your vet now to plan timing and booking."};
      }
      
      function planText(cat,s){
        const dam=s.dam;
        if(S.species==="horse"){
          if(cat.k==="Low"||cat.k==="Moderate")
            return `<strong>Plan:</strong> Foaling is fast and powerful, so preparation matters more than a "wait and see" approach. Have your vet's number ready, know the red-bag warning sign, and be present or monitored (foaling alarm/camera) at term so help can be summoned within minutes.`;
          return `<strong>Plan:</strong> Elective caesareans in mares are uncommon and carry real risk to mare and foal, so this is about emergency readiness, not booking surgery. Involve your vet now, resolve any twin pregnancy well before term, and arrange for a vet to be on immediate call for the birth. A stuck foal needs hands on it within minutes, not an ambulance-distance away.`;
        }
        if(cat.k==="Low")
          return `<strong>Plan:</strong> Standard birth watch. Know the normal timeline, the warning signs of trouble, and your nearest after-hours vet. Call early if labour stalls.`;
        if(cat.k==="Moderate")
          return `<strong>Plan:</strong> Book a late-pregnancy check. Confirm number and size of babies on imaging, know your emergency vet, and set a clear rule for when to call (e.g. strong straining 20–30 min with nothing produced, or more than 2 hours between babies).`;
        if(cat.k==="High")
          return `<strong>Plan:</strong> Arrange a pre-birth veterinary assessment and discuss a scheduled caesarean vs a closely supervised trial of natural birth with a surgical backup ready. Do not plan to "wait and see" alone.`;
        return `<strong>Plan:</strong> Contact your vet now to plan an elective caesarean, including accurate timing so the babies are mature but the ${dam} does not go into unplanned labour first. Have the surgery booked before the due window.`;
      }
      
      function timingTip(s,score){
        if(score<50) return null;
        if(S.species==="dog")
          return `Timing tip: for an elective caesarean in dogs, don't rely on mating dates alone. Progesterone timing from ovulation (birth ~63 days after the LH surge) plus a late x-ray for fetal count and size gives the safest surgery date.`;
        if(S.species==="cat")
          return `Timing tip: confirm litter size and fetal size on imaging late in pregnancy, and track from the confirmed mating window so an elective caesarean isn't done too early.`;
        if(S.species==="cattle")
          return `Timing tip: monitor the heifer/cow closely around the due date and pre-arrange the caesarean with your vet — an elective section beats an exhausted emergency one for calf survival.`;
        if(S.species==="horse")
          return `Timing tip: equine dystocia is a true emergency — foaling is fast and powerful. Have a vet on immediate call, know red-bag signs, and if twins were ever suspected, resolve it long before term.`;
        return `Timing tip: confirm due dates and, where possible, number and size of babies before term so any surgery is planned rather than a crisis.`;
      }
      
      function render(score,reasons,s,b,n){
        const cat=category(score);
        if(S.species==="horse" && score>=50){
          if(score<72){ cat.h="High risk — get your vet involved now"; }
          else { cat.h="Very high risk — treat as an emergency plan"; }
          cat.p="Foaling is fast and any true obstruction is life-threatening. Elective caesarean is not a routine option in horses, so the priority is expert help on the spot the moment labour starts.";
        }
        const pos=Math.max(2,Math.min(98,score));
        const tip=timingTip(s,score);
        const emergency = s.emergency ? `<div class="timing" style="background:#fdecec;border-color:#f3c2c2;color:#8a1c1c">Note: in horses, birth is fast and any true obstruction is a life-threatening emergency for mare and foal. Planned caesareans are uncommon and high-risk — this tool flags risk, but an equine dystocia needs a vet on the spot immediately.</div>` : "";
      
        const reasonLis = reasons.map(r=>`<li class="${r.kind}">${r.txt}</li>`).join("");
      
        el("result").innerHTML = `
          <div class="verdict" style="background:linear-gradient(135deg,${cat.c},${shade(cat.c)})">
            <div class="cat">${s.label} &middot; Estimated birth risk</div>
            <h2>${cat.h}</h2>
            <p>${cat.p}</p>
            <div class="meter">
              <div class="bar"><div class="mk" style="left:${pos}%"></div></div>
              <div class="scale"><span>Low</span><span>Moderate</span><span>High</span><span>Very high</span></div>
            </div>
          </div>
      
          <div class="block">
            <h3>Why — the vet reasoning</h3>
            <ul class="reasons">${reasonLis}</ul>
          </div>
      
          <div class="block"><div class="plan">${planText(cat,s)}</div></div>
          ${tip?`<div class="block"><div class="timing">${tip}</div></div>`:""}
          ${emergency?`<div class="block">${emergency}</div>`:""}
      
          <div class="disc">
            This calculator is a decision-support guide from ASK A VET&trade;, not a diagnosis. It estimates risk from breed, litter size and history — it cannot assess the individual pelvis, fetal position, calcium status or foetal size on the day. Every pregnant animal near term should be assessed by a veterinarian, and any prolonged straining, green/black discharge before the first baby (or red-bag in mares), a baby stuck in the canal, or a distressed mother is an emergency. Always confirm timing and any surgical plan with your own vet.
          </div>
        `;
        el("result").classList.remove("hidden");
        el("result").scrollIntoView({behavior:"smooth",block:"start"});
      }
      
      /* darken a css color for gradient end (works on our known hex vars via canvas) */
      function shade(cssColor){
        const map={"var(--brc-low)":"#0f7a37","var(--brc-moderate)":"#a85e04","var(--brc-high)":"#b13f08","var(--brc-very-high)":"#a11414"};
        return map[cssColor]||cssColor;
      }
      
      /* ---------------- WIRING ---------------- */
      el("calcBtn").onclick=()=>{
        const err=el("errMsg");
        if(!S.species){ err.textContent="Pick a species first."; err.classList.remove("hidden"); return; }
        err.classList.add("hidden");
        calc();
      };
      el("resetBtn").onclick=()=>{
        S={species:null};
        document.querySelectorAll(".sp").forEach(n=>n.classList.remove("on"));
        ["cardBreed","cardLitter","cardFactors","result"].forEach(id=>el(id).classList.add("hidden"));
        el("count").value="";
        window.scrollTo({top:0,behavior:"smooth"});
      };
    }
  }

  if (!customElements.get('s-birth-risk-calculator')) {
    customElements.define('s-birth-risk-calculator', BirthRiskCalculator);
  }
})();
